import {
  EgmDisclosureType,
  InvestmentWireBeneficiaryInput,
  SubmitInvestmentWireInput,
  type AddressInput,
  type BankAccountType,
  type Money,
  type NameInput,
  type PendingWireInput,
  type PendingWireInputV2,
  type SubmitWireInput,
  type SubmitWireInputV2,
  type Wire,
  type WireBeneficiaryInput,
  type submitWireInputV3,
} from '@egw/api'
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY_CODE } from '@egw/constants'
import { type TFunction } from '@egw/i18n'
import { Currency, formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { type FieldValues } from 'react-hook-form'
import { nullTransferFieldOptionv2 } from '../../constants'
import {
  InvestmentWireTransferField,
  InvestmentWireTransferFormInterface,
  WireTransferField,
  type WireTransferFormInterface,
} from '../../types'

interface TDefaultValues {
  t: TFunction
  data?: Wire
  accountType?: BankAccountType
  accountName?: string
}

// Gets Display Name if Business or First + Last Name Joined
export const getDisplayValue = ({
  recipientIsBusiness,
  firstName,
  lastName,
  businessName,
}: TGetDisplayValue): string => {
  const fullName = Boolean(firstName) && Boolean(lastName) ? firstName!.concat(' ', lastName!) : ''
  return recipientIsBusiness && businessName ? businessName : fullName
}

const buildBaseDefaultValues = () => ({
  [WireTransferField.from]: nullTransferFieldOptionv2,
  [WireTransferField.recipientIsBusiness]: false,
  [WireTransferField.id]: undefined,
  [WireTransferField.country]: DEFAULT_COUNTRY,
})

export const buildDefaultValues = ({ data }: TDefaultValues): WireTransferFormInterface => {
  const baseDefaultValues = buildBaseDefaultValues()

  // Declaring since used in multiple places
  const recipientIsBusiness = Boolean(data?.to?.businessName ?? '')
  const firstName = data?.to?.name?.firstName ?? ''
  const lastName = data?.to?.name?.lastName ?? ''
  const businessName = data?.to?.businessName ?? ''

  // Converts amount number back to currency for input
  const amount = data?.money.amount
    ? formatInputToCurrency(Currency.integerCents.omitSign(data.money.amount), true)
    : undefined

  return {
    ...baseDefaultValues,

    // Override Id value w/ Data ** IMPORTANT THIS IS UNDEFINED NOT NULL **
    [WireTransferField.id]: data?.id,

    // Wire Transfer Page
    [WireTransferField.amount]: amount,
    [WireTransferField.reference]: data?.reference ?? '',

    // Recipient Account Info
    [WireTransferField.recipientInfo]: getDisplayValue({
      recipientIsBusiness,
      firstName,
      lastName,
      businessName,
    }),
    [WireTransferField.beneficiaryRoutingNumber]: data?.to?.beneficiaryRoutingNumber ?? '',
    [WireTransferField.beneficiaryAccountNumber]: data?.to?.beneficiaryAccountNumber ?? '',
    [WireTransferField.beneficiaryVerifyAccountNumber]: data?.to?.beneficiaryAccountNumber ?? '',
    [WireTransferField.recipientIsBusiness]: recipientIsBusiness,

    // Recipient Info
    [WireTransferField.businessName]: businessName,
    [WireTransferField.beneficiaryFirstName]: firstName,
    [WireTransferField.beneficiaryLastName]: lastName,
    [WireTransferField.line1]: data?.to?.address.line1 ?? '',
    [WireTransferField.city]: data?.to?.address.city ?? '',
    [WireTransferField.region]: data?.to?.address.region ?? '',
    [WireTransferField.postalCode]: data?.to?.address.postalCode ?? '',
    [WireTransferField.hasIntermediaryBank]: Boolean(data?.to?.intermediaryRoutingNumber),
    [WireTransferField.intermediaryRoutingNumber]: data?.to?.intermediaryRoutingNumber ?? '',
  }
}

export const buildDefaultValuesJoint = (): WireTransferFormInterface => {
  const baseDefaultValues = buildBaseDefaultValues()

  // Declaring since used in multiple places
  const recipientIsBusiness = false
  const firstName = ''
  const lastName = ''
  const businessName = ''

  // Converts amount number back to currency for input
  const amount = undefined

  return {
    ...baseDefaultValues,

    // Override Id value w/ Data ** IMPORTANT THIS IS UNDEFINED NOT NULL **
    [WireTransferField.id]: undefined,

    // Wire Transfer Page
    [WireTransferField.amount]: amount,
    [WireTransferField.reference]: '',

    // Recipient Account Info
    [WireTransferField.recipientInfo]: getDisplayValue({
      recipientIsBusiness,
      firstName,
      lastName,
      businessName,
    }),
    [WireTransferField.beneficiaryRoutingNumber]: '',
    [WireTransferField.beneficiaryAccountNumber]: '',
    [WireTransferField.beneficiaryVerifyAccountNumber]: '',
    [WireTransferField.recipientIsBusiness]: recipientIsBusiness,

    // Recipient Info
    [WireTransferField.businessName]: businessName,
    [WireTransferField.beneficiaryFirstName]: firstName,
    [WireTransferField.beneficiaryLastName]: lastName,
    [WireTransferField.line1]: '',
    [WireTransferField.city]: '',
    [WireTransferField.region]: '',
    [WireTransferField.postalCode]: '',
    [WireTransferField.hasIntermediaryBank]: false,
    [WireTransferField.intermediaryRoutingNumber]: '',
  }
}

export const buildDefaultValuesInvestment = (): InvestmentWireTransferFormInterface => ({
  [InvestmentWireTransferField.from]: nullTransferFieldOptionv2,
  // Override Id value w/ Data ** IMPORTANT THIS IS UNDEFINED NOT NULL **
  [InvestmentWireTransferField.id]: undefined,
  [InvestmentWireTransferField.country]: DEFAULT_COUNTRY,

  // Wire Transfer Page
  [InvestmentWireTransferField.amount]: undefined,
  // TODO: ADD REFERENCE BACK IN WHEN WE HAVE IT
  // [InvestmentWireTransferField.reference]: '',

  // Recipient Account Info
  [InvestmentWireTransferField.recipientInfo]: '',
  [InvestmentWireTransferField.beneficiaryRoutingNumber]: '',
  [InvestmentWireTransferField.beneficiaryAccountNumber]: '',
  [InvestmentWireTransferField.beneficiaryVerifyAccountNumber]: '',

  // Recipient Info
  [InvestmentWireTransferField.beneficiaryAccountTitle]: '',
  [InvestmentWireTransferField.line1]: '',
  [InvestmentWireTransferField.city]: '',
  [InvestmentWireTransferField.region]: '',
  [InvestmentWireTransferField.postalCode]: '',
})

export const buildPendingWireInput = (data: FieldValues): PendingWireInput => {
  const { id = null, reference } = data

  const money = buildMoney(data)
  const to = buildWireBeneficiary(data)

  return {
    id,
    money,
    to,
    reference,
  }
}

export const buildPendingWireInputV2 = (data: FieldValues, accountAlias?: number): PendingWireInputV2 => {
  return { ...buildPendingWireInput(data), accountAlias: accountAlias ?? 0 }
}

const buildWireBeneficiary = (data: FieldValues): WireBeneficiaryInput => {
  const { beneficiaryRoutingNumber, beneficiaryAccountNumber } = data

  const name = buildName(data)
  const businessName = buildBusinessName(data) as string | undefined
  const intermediaryRoutingNumber = buildIntermediaryRoutingNumber(data) as string | undefined
  const address = buildAddress(data)

  return {
    name,
    address,
    businessName,
    beneficiaryRoutingNumber,
    beneficiaryAccountNumber,
    intermediaryRoutingNumber,
  }
}

const buildAddress = (data: FieldValues): AddressInput => {
  const { line1, city, postalCode, region, country } = data

  return {
    line1,
    line2: undefined,
    city,
    region,
    postalCode,
    country,
  }
}

const buildIntermediaryRoutingNumber = (data: FieldValues): string | null => {
  const { hasIntermediaryBank, intermediaryRoutingNumber } = data

  return hasIntermediaryBank ? intermediaryRoutingNumber : null
}

const buildName = (data: FieldValues): NameInput | undefined => {
  const { beneficiaryFirstName: firstName, beneficiaryLastName: lastName, recipientIsBusiness } = data
  return recipientIsBusiness ? undefined : { firstName, lastName }
}

const buildBusinessName = (data: FieldValues): string | null => {
  const { recipientIsBusiness, businessName } = data
  return recipientIsBusiness ? businessName : null
}

const buildMoney = (data: FieldValues): Money => {
  return {
    currencyCode: DEFAULT_CURRENCY_CODE,
    amount: removeCurrencySymbols(data.amount),
  }
}

interface TGetDisplayValue {
  recipientIsBusiness: boolean
  firstName?: string | null
  lastName?: string | null
  businessName?: string | null
}

// Breaks down the wire for review
export const breakdownWireForReviewPage = (data: Wire) => {
  const { reference, to, status, isSubmitted, id } = data

  const recipient = getDisplayValue({
    recipientIsBusiness: Boolean(to?.businessName),
    firstName: to?.name?.firstName,
    lastName: to?.name?.lastName,
    businessName: to?.businessName,
  })

  return {
    id,
    reference,
    amount: data.money.amount,
    intermediaryRoutingNumber: to?.intermediaryRoutingNumber,
    beneficiaryRoutingNumber: to?.beneficiaryRoutingNumber,
    beneficiaryAccountNumber: to?.beneficiaryAccountNumber,
    recipient,
    status,
    isSubmitted,
    accountAlias: data.accountAlias ?? null,
  }
}

interface TBuildSubmitWireInput {
  wireData: Wire
}

export const buildSubmitWireInput = ({ wireData }: TBuildSubmitWireInput): SubmitWireInput => {
  const { id, money } = wireData

  return {
    disclosure: {
      disclosureType: EgmDisclosureType.WIRE_TRANSFER_AGREEMENT,
    },
    id,
    money,
    to: wireData.to!,
    reference: wireData.reference!,
  }
}

export const buildSubmitWireV2Input = ({ wireData }: TBuildSubmitWireInput): SubmitWireInputV2 => {
  const { id, money } = wireData

  return {
    disclosure: {
      disclosureType: EgmDisclosureType.WIRE_TRANSFER_AGREEMENT,
    },
    id,
    money,
    to: wireData.to!,
    reference: wireData.reference!,
    accountAlias: wireData.accountAlias,
  }
}

export const buildSubmitWireV3Input = ({
  wireFormData,
}: {
  wireFormData: WireTransferFormInterface
}): submitWireInputV3 => {
  return {
    disclosure: {
      disclosureType: EgmDisclosureType.WIRE_TRANSFER_AGREEMENT,
    },
    money: {
      currencyCode: DEFAULT_CURRENCY_CODE,
      amount: removeCurrencySymbols(wireFormData.amount ?? ''),
    },
    to: buildWireBeneficiary(wireFormData),
    reference: wireFormData.reference,
    accountAlias: wireFormData.from.alias,
  }
}

export const buildInvestmentWireBeneficiary = (data: FieldValues): InvestmentWireBeneficiaryInput => {
  return {
    accountNumber: data[InvestmentWireTransferField.beneficiaryAccountNumber] as string,
    accountTitle: data[InvestmentWireTransferField.beneficiaryAccountTitle] as string,
    accountAddress: buildAddress(data),
  }
}

export const buildSubmitInvestmentWireInput = ({
  wireFormData,
}: {
  wireFormData: InvestmentWireTransferFormInterface
}): SubmitInvestmentWireInput => {
  return {
    disclosure: {
      disclosureType: EgmDisclosureType.WIRE_TRANSFER_AGREEMENT,
    },
    money: {
      currencyCode: DEFAULT_CURRENCY_CODE,
      amount: removeCurrencySymbols(wireFormData.amount ?? ''),
    },
    accountAlias: wireFormData.from.alias,
    beneficiary: buildInvestmentWireBeneficiary(wireFormData),
    recipientBank: {
      bankId: wireFormData[InvestmentWireTransferField.beneficiaryRoutingNumber] as string,
    },
  }
}
